import {Component, Inject, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {FormControl, FormGroup, Validators} from '@angular/forms';
import {MAT_DIALOG_DATA, MatDialogRef} from '@angular/material/dialog';
import {AppStore} from '../../../store/store';
import {UserModel} from '../../../user/user.model';
import {userIsAdminSelector, userSelector} from '../../../user/user.reducer';
import {SegmentGlobalToggleComponent} from '../../global-toggle/global-toggle.component';
import {SegmentService} from '../../segment.service';
import {Segment, SegmentModel} from '../../segment.model';
import {first, map, startWith} from 'rxjs/operators';
import {Subscription} from 'rxjs';
import {SubSink} from 'subsink';
import {ofType} from '@ngrx/effects';
import {segmentCreated, segmentCreationError} from '../../segments.state';
import {ActionsSubject, Store} from '@ngrx/store';
import {ApiVersion} from '../../../app.types';
import {
  selectBrands,
  selectCategories,
  selectSegmentAudienceTypes,
  selectSegmentMainTypes,
  selectSegmentSubTypes
} from '../../../filters-config/filters-config.selectors';

export interface SegmentCreationData {
  apiVersion: ApiVersion;
}

const MAX_NAME_CHARACTERS = 64;
const DEFAULT_DAYS = 14;
const DEFAULT_PAGE_COUNT = 2;

@Component({
  selector: 'app-segment-create',
  template: `
    <form [formGroup]="segmentCreateForm" (submit)="createSegment()" id="form-create-segment">
      <button mat-icon-button
              id="button-dialog-close"
              class="mat-icon-button-small"
              data-cy="button-dialog-close"
              mat-dialog-close>
        <mat-icon>clear</mat-icon>
      </button>
      <h5 mat-dialog-title>Create Segment</h5>
      <div mat-dialog-content class="mat-body-1">
        <p>Create a new Segment. <span *ngIf="isAdmin">If you want to share this segment with other profiles,
        check the "global" checkbox.</span></p>
        <p *ngIf="errorMessage" class="error" id="error-feedback">{{ errorMessage }}</p>

        <mat-form-field>
          <mat-label>Main Type</mat-label>
          <input [formControl]="mainTypeControl"
                 matInput
                 appClearSpacesInAutocomplete
                 [matAutocomplete]="autoCompleteMainType"
                 data-cy="segment-type-control"
          />
          <mat-autocomplete #autoCompleteMainType="matAutocomplete"
                            (optionSelected)="mainTypeChangeHandler($event.option.value)">
            <mat-option *ngFor="let option of filteredSegmentMainTypesOptions | async" [value]="option"
                        data-cy="segment-type-control-select-option">
              {{ option }}
            </mat-option>
          </mat-autocomplete>
        </mat-form-field>

        <mat-form-field>
          <mat-label>Sub Type</mat-label>
          <input [formControl]="subTypeControl"
                 matInput
                 appClearSpacesInAutocomplete
                 [matAutocomplete]="autoCompleteSubType"
          />
          <mat-autocomplete #autoCompleteSubType="matAutocomplete"
                            (optionSelected)="subTypeChangeHandler($event.option.value)">
            <mat-option *ngFor="let option of filteredSegmentSubTypesOptions | async" [value]="option">
              {{ option }}
            </mat-option>
          </mat-autocomplete>
        </mat-form-field>

        <mat-form-field>
          <mat-label>Audience Type</mat-label>
          <input [formControl]="audienceTypeControl"
                 matInput
                 appClearSpacesInAutocomplete
                 [matAutocomplete]="autoCompleteAudienceType"
          />
          <mat-autocomplete #autoCompleteAudienceType="matAutocomplete">
            <mat-option *ngFor="let option of filteredSegmentAudienceTypesOptions | async" [value]="option">
              {{ option }}
            </mat-option>
          </mat-autocomplete>
        </mat-form-field>

        <ng-container *ngIf="parentSegmentControl.enabled">
          <p>If there's a relation in segment-config between segments, you can select a 'parent' segment
            here that the current segment is dependant on</p>
          <app-parent-segment [parentSegmentControl]="parentSegmentControl"
                              (parentSelectionChanged)="onParentSelectionChanged($event)"
                              data-cy="parent-control">
          </app-parent-segment>
        </ng-container>

        <ng-container *ngIf="taxonomyControl.enabled">
          <app-taxonomy-dropdown [taxonomyControl]="taxonomyControl"
                                 data-cy="taxonomy-control">
          </app-taxonomy-dropdown>
        </ng-container>

        <ng-container *ngIf="brandControl.enabled">
          <mat-form-field>
            <input [formControl]="brandControl" matInput appClearSpacesInAutocomplete placeholder="Brand"
                   [matAutocomplete]="autoBrand"/>
            <mat-autocomplete #autoBrand="matAutocomplete">
              <mat-option *ngFor="let option of filteredBrandOptions | async" [value]="option">
                {{ option }}
              </mat-option>
            </mat-autocomplete>
            <mat-error *ngIf="brandControl.hasError('required')">
              Brand is empty
            </mat-error>
          </mat-form-field>
        </ng-container>

        <ng-container *ngIf="categoryControl.enabled">
          <mat-form-field>
            <input [formControl]="categoryControl" matInput appClearSpacesInAutocomplete
                   placeholder="Category"
                   [matAutocomplete]="autoCategory"
                   data-cy="input-select-segment-category"
                   id="segment-category"/>
            <mat-autocomplete #autoCategory="matAutocomplete">
              <mat-option *ngFor="let option of filteredCategoryOptions | async"
                          [value]="option"
                          data-cy="input-select-option-segment-category">
                {{ option }}
              </mat-option>
            </mat-autocomplete>
            <mat-error *ngIf="categoryControl.hasError('required')">
              Category is empty
            </mat-error>
            <mat-error
              *ngIf="!categoryControl.hasError('required') && categoryControl.hasError('unknownCategory')">
              Only existing categories can be selected
            </mat-error>
          </mat-form-field>
        </ng-container>

        <ng-container>
          <mat-form-field>
            <input [formControl]="freeTextControl"
                   matInput
                   appClearSpaces
                   placeholder="Free Text"
                   id="segment-free-text"
                   data-cy="input-segment-free-text">
            <mat-error *ngIf="freeTextControl.hasError('required')">
              Free text is empty
            </mat-error>
          </mat-form-field>

          <mat-form-field>
            <input matInput [formControl]="nameControl" placeholder="Name"/>
            <mat-error *ngIf="segmentCreateForm.errors?.['maxCharsExceeded']" class="custom-error">
              The full segment name exceeds the {{ maxNameCharacters }} character limitation
              by {{ nameControl.value.length - maxNameCharacters }} character(s).<br/>
            </mat-error>
          </mat-form-field>
          <button type="button"
                  (click)="generateName()"
                  class="mat-stroked-button"
                  data-cy="create-segment-name-generator">Generate name
          </button>

          <ng-container *ngIf="descriptionControl.enabled">
            <mat-form-field>
          <textarea #segmentDescription
                    formControlName="description"
                    class="form-input"
                    id="segment-description"
                    data-cy="textarea-segment-description"
                    matInput
                    maxlength="500"
                    placeholder="Segment Description"
                    rows="4"
                    (focus)="focusOnDescription()"
                    (blur)="blurDescription()"></textarea>
              <mat-hint align="end">{{ segmentDescription.value.length }} / 500</mat-hint>
            </mat-form-field>
            <button *ngIf="!descriptionGeneratorActive"
                    id="generator-bttn"
                    class="mat-stroked-button"
                    data-cy="button-description-generator"
                    (click)="activateDefaultDescription()">Generate Default Description
            </button>

            <div id="generator-controls"
                 data-cy="element-generator-controls"
                 *ngIf="descriptionGeneratorActive">
              <mat-form-field>
                <input matInput
                       [autofocus]="true"
                       [formControl]="subjectControl"
                       data-cy="input-segment-subject"
                       placeholder="Subject"/>
              </mat-form-field>
              <mat-form-field>
                <input type="number"
                       matInput
                       [formControl]="daysControl"
                       data-cy="input-segment-days"
                       placeholder="Days"/>
              </mat-form-field>
              <mat-form-field>
                <input type="number"
                       matInput
                       [formControl]="pageCountControl"
                       data-cy="input-segment-page-count"
                       placeholder="Page Count">
              </mat-form-field>
              <mat-slide-toggle [formControl]="predictiveControl"
                                data-cy="toggle-segment-predictive">
                <strong>Segment is predictive</strong>
              </mat-slide-toggle>
            </div>
          </ng-container>

          <app-keyword-chips (tagsChange)="handleKeywordsChange($event)"></app-keyword-chips>

          <ng-container *ngIf="withClassifierControl.enabled">
            <mat-slide-toggle formControlName="withClassifier">
              <strong>With Classifier</strong>
            </mat-slide-toggle>
          </ng-container>

          <app-segment-global-toggle #globalSegmentToggle [newSegment]="true"></app-segment-global-toggle>
        </ng-container>
      </div>
      <div mat-dialog-actions>
        <button [disabled]="segmentCreateForm.invalid"
                type="submit"
                color="primary"
                id="button-create-segment"
                data-cy="button-segment-create"
                mat-raised-button>Create
        </button>
      </div>
    </form>
  `,
  styleUrls: ['create.component.scss']
})
export class SegmentCreateComponent implements OnInit, OnDestroy {

  @ViewChild('globalSegmentToggle') globalSegmentToggle!: SegmentGlobalToggleComponent;
  user!: UserModel;
  segmentModel!: Segment;
  errorMessage!: string;
  subSink = new SubSink();
  isAdmin!: boolean;
  freeTextControl = new FormControl('', Validators.required);
  descriptionControl = new FormControl('');
  subjectControl = new FormControl('');
  daysControl = new FormControl(DEFAULT_DAYS);
  pageCountControl = new FormControl(DEFAULT_PAGE_COUNT);
  predictiveControl = new FormControl(false);
  brandControl = new FormControl({disabled: true, value: ''});
  parentSegmentControl = new FormControl({disabled: true});
  taxonomyControl = new FormControl({disabled: true});
  withClassifierControl = new FormControl({disabled: true, value: false});
  keywordsControl = new FormControl('');
  mainTypeControl = new FormControl();
  subTypeControl = new FormControl();
  audienceTypeControl = new FormControl();
  descriptionGeneratorActive = false;
  defaultDescriptionForm: FormGroup = new FormGroup({
    subject: this.subjectControl,
    days: this.daysControl,
    pageCount: this.pageCountControl,
    predictive: this.predictiveControl,
  });
  descriptionVariablesSubscription!: Subscription;
  descriptionTextAreaSubscription!: Subscription;
  categoryOptions: string[] = [];
  categoryControl = new FormControl({value: ''});
  filteredCategoryOptions = this.categoryControl.valueChanges.pipe(
    startWith(''),
    map((value: string) => {
      const filterValue = value.toLowerCase();
      return this.categoryOptions.filter(option => option.toLowerCase().includes(filterValue));
    })
  );
  brandOptions: string[] = [];
  segmentMainTypes: string[] = [];
  filteredSegmentMainTypesOptions = this.mainTypeControl.valueChanges.pipe(
    startWith(''),
    map((value: string) => {
      const filterValue = value.toLowerCase();
      return this.segmentMainTypes.filter(option => option.toLowerCase().includes(filterValue));
    })
  );
  segmentSubTypes: string[] = [];
  filteredSegmentSubTypesOptions = this.subTypeControl.valueChanges.pipe(
    startWith(''),
    map((value: string) => {
      const filterValue = value.toLowerCase();
      return this.segmentSubTypes.filter(option => option.toLowerCase().includes(filterValue));
    })
  );
  segmentAudienceTypes: string[] = [];
  filteredSegmentAudienceTypesOptions = this.audienceTypeControl.valueChanges.pipe(
    startWith(''),
    map((value: string) => {
      const filterValue = value.toLowerCase();
      return this.segmentAudienceTypes.filter(option => option.toLowerCase().includes(filterValue));
    })
  );
  filteredBrandOptions = this.brandControl.valueChanges.pipe(
    startWith(''),
    map((value: string) => {
      const filterValue = value.toLowerCase();
      return this.brandOptions.filter(option => option.toLowerCase().includes(filterValue));
    })
  );
  nameControl = new FormControl('');
  segmentCreateForm: FormGroup = new FormGroup({
    mainType: this.mainTypeControl,
    subType: this.subTypeControl,
    audienceType: this.audienceTypeControl,
    freeText: this.freeTextControl,
    description: this.descriptionControl,
    partnerId: new FormControl(''),
    withClassifier: this.withClassifierControl,
    brand: this.brandControl,
    category: this.categoryControl,
    keywords: this.keywordsControl,
    parent: this.parentSegmentControl,
    taxonomy: this.taxonomyControl,
    name: this.nameControl
  }, () => {
    return this.nameControl.value?.length > MAX_NAME_CHARACTERS ? {maxCharsExceeded: true} : null;
  });

  constructor(
    private dialogRef: MatDialogRef<SegmentCreateComponent>,
    @Inject(MAT_DIALOG_DATA) public data: SegmentCreationData,
    private appStore: AppStore,
    private store: Store,
    private actions: ActionsSubject,
    private segmentsService: SegmentService) {
    this.mainTypeChangeHandler('Standard');
  }

  get maxNameCharacters() {
    return MAX_NAME_CHARACTERS;
  }

  private static format(variable: string | number, placeholder: string): string | number {
    return variable || placeholder;
  }

  mainTypeChangeHandler(value: string | null = null) {
    if (value === 'Intern') {
      this.descriptionControl.disable();
      this.withClassifierControl.disable();
    } else {
      this.descriptionControl.enable();
      this.withClassifierControl.enable();
    }

    if (value === 'Standard') {
      this.descriptionControl.setValidators(Validators.required);
      this.descriptionControl.updateValueAndValidity();
    } else {
      this.descriptionControl.clearValidators();
      this.descriptionControl.updateValueAndValidity();
    }

    if (this.data.apiVersion === ApiVersion.V2) {
      this.parentSegmentControl.enable();
      this.taxonomyControl.enable();
    } else {
      this.parentSegmentControl.disable();
      this.taxonomyControl.disable();
    }
  }

  subTypeChangeHandler(value: string) {
    if (value === 'Classifier/Validierung') {
      this.descriptionControl.disable();
      this.withClassifierControl.disable();
    } else {
      this.descriptionControl.enable();
      this.withClassifierControl.enable();
    }
    if (value === 'branded') {
      this.brandControl.enable();
    } else {
      this.brandControl.disable();
    }
  }

  ngOnInit() {
    const appState = this.appStore.getState();
    this.user = userSelector(appState);
    this.reset();
    this.store.select(userIsAdminSelector).pipe(first()).subscribe(isAdmin => this.isAdmin = isAdmin);

    this.store.select(selectCategories).pipe(first()).subscribe(val => this.categoryOptions = val);
    this.store.select(selectBrands).pipe(first()).subscribe(val => this.brandOptions = val);
    this.store.select(selectSegmentMainTypes).pipe(first()).subscribe(val => this.segmentMainTypes = val);
    this.store.select(selectSegmentSubTypes).pipe(first()).subscribe(val => this.segmentSubTypes = val);
    this.store.select(selectSegmentAudienceTypes).pipe(first()).subscribe(val => this.segmentAudienceTypes = val);

    this.subSink.sink = this.actions.pipe(ofType(segmentCreated))
      .subscribe(_ => {
        this.dialogRef.close();
      });
    this.subSink.sink = this.actions.pipe(ofType(segmentCreationError))
      .subscribe(action => {
        const segmentCreateErrorState = action.error;
        if (segmentCreateErrorState) {
          if (segmentCreateErrorState.message === 'name not unique') {
            this.errorMessage = 'The segment name must be unique';
          } else {
            this.errorMessage = segmentCreateErrorState.message;
          }
        }
      });
  }

  ngOnDestroy() {
    this.subSink.unsubscribe();
    if (this.descriptionTextAreaSubscription) {
      this.descriptionTextAreaSubscription.unsubscribe();
    }
    if (this.descriptionVariablesSubscription) {
      this.descriptionVariablesSubscription.unsubscribe();
    }
  }

  clear() {
    this.errorMessage = '';
  }

  reset() {
    this.clear();
    this.segmentCreateForm.reset(new SegmentModel({partnerId: this.user.partnerId} as SegmentModel));
  }

  handleKeywordsChange(e: string) {
    this.keywordsControl.setValue(e);
  }

  createSegment() {
    if (this.segmentCreateForm.valid) {
      const withClassifier = !!this.withClassifierControl.value;
      const name = this.nameControl.value;

      const values = {...this.segmentCreateForm.value, name};
      delete values.withClassifier;
      if (withClassifier) {
        const classifierSegment = new SegmentModel({
          mainType: 'Intern',
          subType: 'Classifier/Validierung',
          name: this.mainTypeControl.value !== 'Intern' ? `${name} @classifier` : name,
          partnerId: values.partnerId,
          freeText: values.freeText,
          keywords: values.keywords
        } as SegmentModel);

        this.updatePartnerId(classifierSegment);

        this.segmentsService.createSegment(classifierSegment, (newSegment) => {
          values.classifierId = newSegment.id;
          this.createNonClassifierSegment(values);
        }, this.data.apiVersion);
      } else {
        this.createNonClassifierSegment(values);
      }
    }
    this.clear();
  }

  updatePartnerId(model: Segment) {
    if (this.isAdmin && this.globalSegmentToggle.toggle.checked) {
      // make segment global
      model.partnerId = 0;
    }
  }

  generateName() {
    let prefix = '';
    if (this.mainTypeControl.value === 'Standard' || this.subTypeControl.value === 'branded') {
      prefix = 'emetriq';
    } else if (this.mainTypeControl.value === 'Custom') {
      prefix = 'emetriq custom';
    } else if (this.audienceTypeControl.value === 'Contextual') {
      prefix = 'emetriq contextual';
    } else if (this.mainTypeControl.value === 'app interest') {
      prefix = 'emetriq app interest';
    }

    let category = '';
    if (this.categoryControl?.enabled) {
      category = this.categoryControl.value;
    }

    let postfix = '';
    if (this.brandControl?.enabled) {
      postfix = this.brandControl.value;
    } else if (this.subTypeControl.value === 'Classifier/Validierung') {
      postfix = 'classifier';
    }

    this.nameControl.setValue(`${prefix}${prefix && category ? ' ' : ''}` +
      `${category}${prefix || category ? ': ' : ''}` +
      `${this.freeTextControl?.value ?? ''}${postfix ? ' @' + postfix : ''}`);
  }

  activateDefaultDescription() {
    this.descriptionVariablesSubscription = this.defaultDescriptionForm.valueChanges.pipe(
      startWith(this.defaultDescriptionForm.value as DescriptionVariables))
      .subscribe((values: DescriptionVariables) => {
        this.setDefaultDescription(values);
      });
    this.descriptionGeneratorActive = true;
  }

  focusOnDescription() {
    if (this.descriptionGeneratorActive) {
      this.descriptionTextAreaSubscription = this.descriptionControl.valueChanges.subscribe(() => {
        this.descriptionGeneratorActive = false;
        this.descriptionVariablesSubscription.unsubscribe();
        this.descriptionTextAreaSubscription.unsubscribe();
      });
    }
  }

  blurDescription() {
    this.descriptionTextAreaSubscription?.unsubscribe();
  }

  onParentSelectionChanged($event: Segment) {
    this.freeTextControl.setValue($event.freeText);
    this.categoryControl.setValue($event.category);
    this.descriptionControl.setValue($event.description);
  }

  private createNonClassifierSegment(values: any): void {
    const segmentModel = new SegmentModel(values);
    this.updatePartnerId(segmentModel);
    this.segmentsService.createSegment(segmentModel, undefined, this.data.apiVersion);
  }

  private setDefaultDescription(values: DescriptionVariables) {
    const subject = SegmentCreateComponent.format(values.subject, '#SUBJECT#');
    const days = SegmentCreateComponent.format(values.days, '#DAYS#');
    const pageCount = SegmentCreateComponent.format(values.pageCount, '#PAGE_COUNT#');
    const predictive = values.predictive ? ' Die Hochrechnung erfolgt mittels Lookalike-Modellierung.' : '';
    const defaultDescription = `Beinhaltet Nutzer, die überdurchschnittlich hohes Interesse an ${subject} ` +
      `aufweisen. Der Nutzer muss in den letzten ${days} Tagen mindestens ${pageCount} Webseiten zu ` +
      `dem entsprechenden Thema besucht haben.${predictive}`;
    this.descriptionControl.setValue(defaultDescription);
  }
}

interface DescriptionVariables {
  subject: string;
  days: number;
  pageCount: number;
  predictive: boolean;
}
